/**
 * pagina detalhe de uma pesquisa.
 *  - header com objeto, status, id, datas
 *  - tabela de itens
 *  - acoes: transicionar status (matriz inline — espelha domain), adicionar item
 *  - placeholder pra modulos de cotacao/cesta (PR 1.11)
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  type ItemPesquisaInput,
  ItemPesquisaInputSchema,
  type StatusPesquisaT,
} from '@licita-mind/shared-types';
import { Logo } from '../components/Logo';
import { StatusBadge } from '../components/StatusBadge';
import {
  baixarDocumento,
  useGerarDocumento,
  useGetDocumento,
} from '../hooks/useDocumento';
import {
  useAdicionarItem,
  useGetPesquisa,
  useTransicionarStatus,
} from '../hooks/usePesquisa';
import { ApiError } from '../lib/api';

const STATUS_PERMITE_DOCUMENTO: readonly StatusPesquisaT[] = [
  'CESTA_FORMADA',
  'JUSTIFICATIVA_GERADA',
  'DOCUMENTO_ASSINADO',
  'FINALIZADA',
];

// matriz inline — duplicada do domain (packages/domain/policies/transicoes-pesquisa.ts)
// pra evitar puxar domain inteiro no bundle do web. cobertura: validacao final fica
// no backend, aqui e so pra montar UI.
const TRANSICOES_LICITAS: Readonly<Record<StatusPesquisaT, readonly StatusPesquisaT[]>> = {
  RASCUNHO: ['COLETANDO_PRECOS', 'CANCELADA'],
  COLETANDO_PRECOS: ['CESTA_FORMADA', 'CANCELADA'],
  CESTA_FORMADA: ['JUSTIFICATIVA_GERADA', 'CANCELADA'],
  JUSTIFICATIVA_GERADA: ['DOCUMENTO_ASSINADO', 'CANCELADA'],
  DOCUMENTO_ASSINADO: ['FINALIZADA'],
  FINALIZADA: [],
  CANCELADA: [],
};

const STATUS_EDITAVEL_ITENS: readonly StatusPesquisaT[] = [
  'RASCUNHO',
  'COLETANDO_PRECOS',
];

const LABEL_STATUS: Readonly<Record<StatusPesquisaT, string>> = {
  RASCUNHO: 'rascunho',
  COLETANDO_PRECOS: 'coletando preços',
  CESTA_FORMADA: 'cesta formada',
  JUSTIFICATIVA_GERADA: 'justificativa gerada',
  DOCUMENTO_ASSINADO: 'documento assinado',
  FINALIZADA: 'finalizada',
  CANCELADA: 'cancelada',
};

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const PesquisaDetalhePage = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { data, isPending, error } = useGetPesquisa(id);
  const transicao = useTransicionarStatus(id ?? '');
  const adicionar = useAdicionarItem(id ?? '');
  const { data: documento } = useGetDocumento(id);
  const gerarDoc = useGerarDocumento(id ?? '');
  const [downloadErro, setDownloadErro] = useState<string | null>(null);

  const [showItemForm, setShowItemForm] = useState(false);
  const [novoItem, setNovoItem] = useState<ItemPesquisaInput>({
    descricao: '',
    unidade: 'UN',
    quantidade: 1,
    catmat: null,
    catser: null,
    especificacao_tecnica: null,
  });
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});

  if (isPending) {
    return (
      <Layout>
        <p className="text-center text-navy-500 text-sm py-12">carregando...</p>
      </Layout>
    );
  }
  if (error) {
    const ae = error as Error & { status?: number };
    return (
      <Layout>
        <div className="card text-center py-12">
          <h3 className="font-display font-semibold text-lg">não encontrei essa pesquisa</h3>
          <p className="text-navy-500 text-sm mt-2">{ae.message}</p>
          <Link to="/" className="btn-secondary text-sm mt-4 inline-block">
            voltar pra lista
          </Link>
        </div>
      </Layout>
    );
  }
  if (!data) return null;

  const transicoes = TRANSICOES_LICITAS[data.status];
  const podeEditarItens = STATUS_EDITAVEL_ITENS.includes(data.status);
  const transicaoErr = transicao.error instanceof ApiError ? transicao.error : null;
  const adicionarErr = adicionar.error instanceof ApiError ? adicionar.error : null;

  const submitItem = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setItemErrors({});
    const limpo: ItemPesquisaInput = {
      ...novoItem,
      catmat: novoItem.catmat?.trim() || null,
      catser: novoItem.catser?.trim() || null,
      especificacao_tecnica: novoItem.especificacao_tecnica?.trim() || null,
    };
    const parsed = ItemPesquisaInputSchema.safeParse(limpo);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        flat[issue.path.join('.')] = issue.message;
      }
      setItemErrors(flat);
      return;
    }
    adicionar.mutate(parsed.data, {
      onSuccess: () => {
        setShowItemForm(false);
        setNovoItem({
          descricao: '',
          unidade: 'UN',
          quantidade: 1,
          catmat: null,
          catser: null,
          especificacao_tecnica: null,
        });
      },
    });
  };

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/" className="text-sm text-navy-500 hover:text-navy-700">
          ← pesquisas
        </Link>
      </div>

      <div className="card mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-display font-semibold mb-2 break-words">
              {data.objeto}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-sm text-navy-500">
              <StatusBadge status={data.status} />
              <span className="text-xs font-mono">{data.id}</span>
              <span>criada {fmtDateTime(data.criada_em)}</span>
            </div>
          </div>
        </div>

        {transicoes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-navy-100">
            <p className="text-xs font-medium text-navy-500 uppercase mb-2">transicionar para</p>
            <div className="flex flex-wrap gap-2">
              {transicoes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => transicao.mutate(s)}
                  disabled={transicao.isPending}
                  className="btn-secondary text-sm"
                >
                  {LABEL_STATUS[s]}
                </button>
              ))}
            </div>
            {transicaoErr && (
              <p className="error mt-2">
                {transicaoErr.code}: {transicaoErr.message}
              </p>
            )}
          </div>
        )}
        {transicoes.length === 0 && (
          <div className="mt-4 pt-4 border-t border-navy-100 text-xs text-navy-500">
            status terminal — não admite transições.
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg">itens ({data.itens.length})</h3>
          {podeEditarItens && !showItemForm && (
            <button
              type="button"
              onClick={() => setShowItemForm(true)}
              className="btn-secondary text-sm"
            >
              + adicionar item
            </button>
          )}
        </div>

        {showItemForm && podeEditarItens && (
          <form
            onSubmit={submitItem}
            className="border border-navy-200 rounded-md p-4 mb-4 bg-navy-50/30 space-y-3"
          >
            {adicionarErr && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
                {adicionarErr.code}: {adicionarErr.message}
              </div>
            )}
            <div>
              <label className="label">descrição</label>
              <input
                className="input"
                value={novoItem.descricao}
                onChange={(e) => setNovoItem((v) => ({ ...v, descricao: e.target.value }))}
                required
              />
              {itemErrors.descricao && <p className="error">{itemErrors.descricao}</p>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">unidade</label>
                <input
                  className="input"
                  value={novoItem.unidade}
                  onChange={(e) => setNovoItem((v) => ({ ...v, unidade: e.target.value }))}
                  required
                  maxLength={20}
                />
              </div>
              <div>
                <label className="label">quantidade</label>
                <input
                  type="number"
                  className="input"
                  min="0.0001"
                  step="any"
                  value={novoItem.quantidade}
                  onChange={(e) =>
                    setNovoItem((v) => ({ ...v, quantidade: Number(e.target.value) }))
                  }
                  required
                />
              </div>
              <div>
                <label className="label">CATMAT</label>
                <input
                  className="input font-mono"
                  value={novoItem.catmat ?? ''}
                  onChange={(e) => setNovoItem((v) => ({ ...v, catmat: e.target.value }))}
                  pattern="[0-9]{6,12}"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowItemForm(false)}
                className="btn-ghost text-sm"
              >
                cancelar
              </button>
              <button type="submit" className="btn-primary text-sm" disabled={adicionar.isPending}>
                {adicionar.isPending ? 'adicionando...' : 'adicionar'}
              </button>
            </div>
          </form>
        )}

        {data.itens.length === 0 ? (
          <p className="text-sm text-navy-500 py-8 text-center">
            ainda sem itens. {podeEditarItens && 'adicione o primeiro acima.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-navy-500 uppercase border-b border-navy-100">
              <tr>
                <th className="text-left py-2 font-medium">descrição</th>
                <th className="text-left py-2 font-medium">CATMAT/CATSER</th>
                <th className="text-right py-2 font-medium">unidade</th>
                <th className="text-right py-2 font-medium">quantidade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {data.itens.map((it) => (
                <tr
                  key={it.id}
                  className="hover:bg-navy-50 cursor-pointer"
                  onClick={() => nav(`/pesquisas/${id}/itens/${it.id}`)}
                >
                  <td className="py-3">
                    <div className="font-medium">{it.descricao}</div>
                    {it.especificacao_tecnica && (
                      <div className="text-xs text-navy-500 mt-1">{it.especificacao_tecnica}</div>
                    )}
                  </td>
                  <td className="py-3 font-mono text-xs">{it.catmat ?? it.catser ?? '—'}</td>
                  <td className="py-3 text-right">{it.unidade}</td>
                  <td className="py-3 text-right tabular-nums">{it.quantidade}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-center text-xs text-navy-500 mt-8 mb-6">
        clique num item pra registrar cotações e formar cesta
      </p>

      {STATUS_PERMITE_DOCUMENTO.includes(data.status) && (
        <section className="card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-semibold text-lg">documento de pesquisa</h3>
              <p className="text-xs text-navy-500 mt-0.5">
                PDF assinável com cestas, cotações, estatísticas e fundamentação legal
                (IN 65/21 art. 3º)
              </p>
            </div>
            <div className="flex gap-2">
              {documento && (
                <button
                  type="button"
                  onClick={() => {
                    setDownloadErro(null);
                    baixarDocumento(id ?? '').catch((e) => setDownloadErro((e as Error).message));
                  }}
                  className="btn-secondary text-sm"
                >
                  ⬇ baixar PDF
                </button>
              )}
              <button
                type="button"
                onClick={() => gerarDoc.mutate()}
                disabled={gerarDoc.isPending}
                className="btn-primary text-sm"
              >
                {gerarDoc.isPending
                  ? 'gerando...'
                  : documento
                    ? 're-gerar'
                    : 'gerar documento'}
              </button>
            </div>
          </div>

          {gerarDoc.error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-3">
              {(gerarDoc.error as ApiError).code ?? 'ERRO'}:{' '}
              {(gerarDoc.error as ApiError).message}
            </div>
          )}
          {downloadErro && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-3">
              {downloadErro}
            </div>
          )}

          {documento ? (
            <div className="bg-navy-50/50 border border-navy-100 rounded p-3 text-xs font-mono space-y-1">
              <div>
                <span className="text-navy-500">id:</span> {documento.id}
              </div>
              <div>
                <span className="text-navy-500">SHA-256:</span>{' '}
                <span className="break-all">{documento.hash_sha256}</span>
              </div>
              <div>
                <span className="text-navy-500">gerado em:</span>{' '}
                {fmtDateTime(documento.gerado_em)}
              </div>
              <div>
                <span className="text-navy-500">tamanho:</span>{' '}
                {(documento.tamanho_bytes / 1024).toFixed(1)} KB
              </div>
            </div>
          ) : (
            <p className="text-sm text-navy-500 text-center py-4">
              ainda não gerado — clique em "gerar documento"
            </p>
          )}
        </section>
      )}
    </Layout>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen flex flex-col">
    <header className="bg-white border-b border-navy-100">
      <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" aria-label="ir pra lista">
          <Logo />
        </Link>
      </div>
    </header>
    <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">{children}</main>
    <footer className="bg-white border-t border-navy-100 py-4">
      <p className="text-center text-xs text-navy-400">by lions intelligence</p>
    </footer>
  </div>
);
